import { useState } from "react";
import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n, getLocale } from "@/lib/i18n";
import { useReviews, useReviewSummary, submitReview } from "@/lib/reviews";
import { cleanTitle } from "@/lib/api";
import { Stars } from "./ProductCard";
import { SplitText } from "@/components/fx/Motion";
import { PhotoPicker, ReviewPhotos } from "./ReviewPhotos";

const RatingInput = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => onChange(i)} className="text-gold hover:scale-125 transition-transform">
        <Star size={26} fill={i <= value ? "currentColor" : "none"} strokeWidth={1.4} />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ product, open, setOpen }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", email: "", text: "", rating: 0, website: "" });
  const [state, setState] = useState("idle");
  const [photos, setPhotos] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "w-full bg-transparent border border-white/15 px-4 text-sm text-white focus:outline-none focus:border-[#c9a55c]";

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || !f.rating || f.text.trim().length < 10) return setState("invalid");
    setState("sending");
    try {
      await submitReview({ ...f, email: f.email || null, photo_ids: photos.map((x) => x.id), product_handle: product.handle, product_title: product.title_en || product.title });
      setState("done");
    } catch {
      setState("invalid");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && state === "done") { setState("idle"); setPhotos([]); setF({ name: "", email: "", text: "", rating: 0, website: "" }); } }}>
      <DialogContent data-testid="review-form-dialog" className="bg-[#0e0d0b] border border-[#c9a55c]/30 max-w-lg max-h-[92vh] overflow-y-auto" data-lenis-prevent>
        <DialogTitle className="font-display text-3xl text-white">{t("review_form_title")}</DialogTitle>
        <DialogDescription className="text-white/50 text-sm">{cleanTitle(product.title)} · {t("review_form_text")}</DialogDescription>
        {state === "done" ? (
          <motion.p data-testid="review-form-success" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-gold py-8 text-center">{t("review_thanks")}</motion.p>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div><p className="text-xs text-white/60 mb-2">{t("review_rating")}</p><RatingInput value={f.rating} onChange={(rating) => setF({ ...f, rating })} /></div>
            <input data-testid="review-name-input" value={f.name} onChange={set("name")} maxLength={60} placeholder={t("review_name")} className={`${input} h-12`} />
            <input data-testid="review-email-input" type="email" value={f.email} onChange={set("email")} placeholder={t("review_email")} className={`${input} h-12`} />
            <textarea data-testid="review-text-input" value={f.text} onChange={set("text")} maxLength={1500} rows={4} placeholder={t("review_text")} className={`${input} py-3 resize-none`} />
            <PhotoPicker photos={photos} setPhotos={setPhotos} />
            <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden />
            {state === "invalid" && <p data-testid="review-form-error" className="text-xs text-red-400">{t("review_error")}</p>}
            <button data-testid="review-submit-button" disabled={state === "sending"} className="btn-gold w-full h-14">{state === "sending" ? t("review_sending") : t("review_submit")}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const RatingSummary = ({ handle, size = 15, showEmpty }) => {
  const { t } = useI18n();
  const { data = {} } = useReviewSummary();
  const s = data[handle];
  if (!s) return showEmpty ? <span className="text-xs text-white/50">{t("be_first")}</span> : null;
  return (
    <div data-testid={`rating-summary-${handle}`} className="flex items-center gap-2">
      <Stars value={Math.round(s.avg)} size={size} />
      <span className="text-[11px] text-white/50">{s.avg} · {s.count === 1 ? t("reviews_count_one") : t("reviews_count", { n: s.count })}</span>
    </div>
  );
};

export const ProductReviews = ({ product }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { data = [] } = useReviews(product.handle);
  const qc = useQueryClient();
  const setOpenAndRefresh = (o) => { setOpen(o); if (!o) qc.invalidateQueries({ queryKey: ["reviews"] }); qc.invalidateQueries({ queryKey: ["reviews-summary"] }); };

  return (
    <section data-testid="product-reviews-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="eyebrow mb-4">{t("reviews_eyebrow")}</p>
          <h2 className="font-display text-3xl lg:text-4xl"><SplitText text={t("customer_reviews")} /></h2>
          <div className="mt-4"><RatingSummary handle={product.handle} showEmpty /></div>
        </div>
        <button data-testid="write-review-button" onClick={() => setOpen(true)} className="btn-ghost h-12 px-8">{t("write_review")}</button>
      </div>
      {data.length ? (
        <div className="grid md:grid-cols-2 gap-5">
          {data.map((r, i) => (
            <motion.figure key={r.id} data-testid={`product-review-${r.id}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="border border-white/10 p-6 bg-[#0e0d0b]">
              <Stars value={r.rating} />
              <blockquote className="mt-3 mb-4 text-white/85 leading-relaxed">“{r.text}”</blockquote>
              <ReviewPhotos photos={r.photos} testid={`product-review-photos-${r.id}`} />
              <figcaption className="text-xs text-white/50"><span className="text-white">{r.name}</span> · {new Date(r.created_at).toLocaleDateString(getLocale())} · <span className="text-gold">{t("verified")}</span></figcaption>
            </motion.figure>
          ))}
        </div>
      ) : <p data-testid="no-reviews" className="text-sm text-white/40">{t("no_reviews_yet")}</p>}
      <ReviewForm product={product} open={open} setOpen={setOpenAndRefresh} />
    </section>
  );
};
